import React, { useEffect, useState } from 'react';
import { deleteField, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { HCard, SectionLabel, HPrimaryButton } from '../h';
import { Switch } from '../ui/switch';
import { HField } from '../h/inputs';
import { normalizePhone, formatPhoneForDisplay, SUPPORTED_COUNTRIES } from '../../lib/phone';
import { cn } from '../../lib/utils';

/* SMS Higlo notifications, shown in the Hearth detail view BELOW the "Higlos
   this Hearth is in" section. Receive-only opt-in, per Hearth, at
   deviceManagement/{familyID}/userPreferences/notifications (controller-writable):
   phone number + master enable + two prefs —
   - joinNotificationType: 'pingOnFirstJoin' | 'pingOnEveryJoin' (default every) —
     one text for the first arrival vs a text per later arrival.
   - pingOnEmpty (default off): text me when a gathering ends, only if I didn't join.
   Turning off clears the number + prefs; a Firestore trigger retains the consent
   record (with a hashed number + revokedAt) and sends a confirmation text on
   enable / number change. All sending is server-decided (activityNotices).

   Consent text is stored verbatim with a version number — it doubles as the
   opt-in record for carrier compliance. Bump CONSENT_TEXT_VERSION if it changes. */

const CONSENT_TEXT_VERSION = 1;
// US (two-way toll-free sender): STOP/HELP is the carrier-level opt-out.
const CONSENT_TEXT_US =
  'By turning this on you agree to receive automated text messages from Higlo ' +
  'about activity in your Higlos, at the number provided. Message frequency ' +
  'varies. Message & data rates may apply. Reply STOP to stop, HELP for help.';
// UK (one-way alphanumeric "Higlo" sender — can't receive STOP): the account
// portal toggle is the off switch instead.
const CONSENT_TEXT_UK =
  'By turning this on you agree to receive automated text messages from Higlo ' +
  'about activity in your Higlos, at the number provided. Message frequency ' +
  'varies. You can turn these off any time from your account portal.';

function consentTextFor(country) {
  return country === 'UK' ? CONSENT_TEXT_UK : CONSENT_TEXT_US;
}

// Flag emoji per supported country (shown instead of the country name).
const COUNTRY_FLAG = { US: '🇺🇸', UK: '🇬🇧' };

function PrefRow({ title, subtitle, control, divider }) {
  return (
    <div
      className={cn(
        'px-4 flex justify-between gap-3',
        // With a subtitle the row is multi-line, so top-align with roomier
        // padding; a single-line row (no subtitle) centres its label and sits
        // tighter so the two option rows aren't far apart.
        subtitle ? 'items-start py-3.5' : 'items-center py-2.5',
        divider && 'border-b border-slate-100',
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-slate-900">{title}</div>
        {subtitle && (
          <div className="text-[12px] text-slate-500 leading-relaxed mt-0.5">{subtitle}</div>
        )}
      </div>
      <div className={cn('flex-shrink-0', subtitle && 'mt-0.5')}>{control}</div>
    </div>
  );
}

export function SmsNotificationsCard({ familyID, accountUUID, userEmail }) {
  const [settings, setSettings] = useState(null); // receive opt-in doc; null = loading
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false); // read failed before any data

  // Phone entry form (shown whenever notifications are off). To change a number
  // the user turns off, which drops back to this form.
  const [country, setCountry] = useState('US');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null); // toggle / turn-off failures

  useEffect(() => {
    if (!familyID) return undefined;
    // Reset per-Hearth state so one Hearth's settings never leak onto another.
    setSettings(null);
    setLoaded(false);
    setLoadError(false);
    setPhoneInput('');
    setPhoneError(null);
    setActionError(null);

    const unsub = onSnapshot(
      doc(db, 'deviceManagement', familyID, 'userPreferences', 'notifications'),
      (snap) => {
        setSettings(snap.exists() ? snap.data() : {});
        setLoaded(true);
        setLoadError(false);
      },
      (err) => {
        // Transient read error. Keep the last-known settings mid-session; if we
        // never loaded any, flag it so we show a retry affordance rather than
        // the off-state form (which would look like "never enrolled").
        console.warn('notifications prefs listener', err?.code || err);
        setLoaded(true);
        setLoadError(true);
      },
    );
    return unsub;
  }, [familyID]);

  const savePrefs = async (fields) => {
    await setDoc(
      doc(db, 'deviceManagement', familyID, 'userPreferences', 'notifications'),
      fields,
      { merge: true },
    );
  };

  const handleEnable = async () => {
    const { e164, error } = normalizePhone(phoneInput, country);
    if (error) {
      setPhoneError(error);
      return;
    }
    setPhoneError(null);
    setSaving(true);
    try {
      await savePrefs({
        smsEnabled: true,
        smsPhoneE164: e164,
        // Default to a ping per arrival unless the family already chose otherwise.
        joinNotificationType: settings?.joinNotificationType || 'pingOnEveryJoin',
        pingOnEmpty: settings?.pingOnEmpty === true,
        consent: {
          givenAt: new Date().toISOString(),
          textVersion: CONSENT_TEXT_VERSION,
          text: consentTextFor(country),
          accountUUID: accountUUID || null,
          email: userEmail || null,
          // Clear any prior revocation stamp so an off→on cycle doesn't leave the
          // consent record reading as both granted and revoked.
          revokedAt: deleteField(),
          phoneHash: deleteField(),
        },
        updatedAt: new Date().toISOString(),
      });
      setPhoneInput('');
    } catch (err) {
      console.error('SmsNotificationsCard enable error', err);
      setPhoneError("Couldn't save, please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleTurnOff = async () => {
    setActionError(null);
    try {
      // Off clears the number + prefs (the server retains the consent record
      // with a hashed number + revokedAt).
      await savePrefs({
        smsEnabled: false,
        smsPhoneE164: deleteField(),
        joinNotificationType: deleteField(),
        pingOnEmpty: deleteField(),
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error('SmsNotificationsCard disable error', err);
      setActionError("Couldn't update, please try again.");
    }
  };

  const handleSetType = async (value) => {
    setActionError(null);
    try {
      await savePrefs({ joinNotificationType: value, updatedAt: new Date().toISOString() });
    } catch (err) {
      console.error('SmsNotificationsCard joinNotificationType error', err);
      setActionError("Couldn't update, please try again.");
    }
  };

  const handleToggleEmpty = async (on) => {
    setActionError(null);
    try {
      await savePrefs({ pingOnEmpty: on, updatedAt: new Date().toISOString() });
    } catch (err) {
      console.error('SmsNotificationsCard pingOnEmpty error', err);
      setActionError("Couldn't update, please try again.");
    }
  };

  if (!loaded) return null;

  // Read failed before any settings loaded: don't present the off-state form as
  // truth (it would look like "never enrolled"). onSnapshot auto-retries.
  if (loadError && settings == null) {
    return (
      <>
        <SectionLabel className="mb-2">Text notifications</SectionLabel>
        <HCard className="mb-4 overflow-hidden">
          <div className="px-4 py-3.5 text-[13px] text-slate-500">
            Couldn&rsquo;t load your notification settings just now, retrying&hellip;
          </div>
        </HCard>
      </>
    );
  }

  const enabled = settings?.smsEnabled === true && settings?.smsPhoneE164;
  const selectedCountry = SUPPORTED_COUNTRIES.find((c) => c.code === country);
  const joinMode = settings?.joinNotificationType === 'pingOnFirstJoin'
    ? 'pingOnFirstJoin' : 'pingOnEveryJoin';

  return (
    <>
      <SectionLabel className="mb-2">Text notifications</SectionLabel>
      <HCard className="mb-4 overflow-hidden">
        {enabled && (
          <>
            {/* Master on/off at the top. To change the number, turn off and back on. */}
            <PrefRow
              divider
              title="Text notifications are on"
              subtitle={
                <>
                  Sent to{' '}
                  <span className="font-medium text-slate-700">
                    {formatPhoneForDisplay(settings.smsPhoneE164)}
                  </span>
                  .
                </>
              }
              control={
                <Switch
                  checked
                  onCheckedChange={handleTurnOff}
                  aria-label="Turn off text notifications"
                />
              }
            />
            {/* "When to be notified" — one line per option, no blurb. */}
            <div className="px-4 pt-3 pb-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
              When to be notified
            </div>
            <PrefRow
              title="When people join"
              control={
                <select
                  value={joinMode}
                  onChange={(e) => handleSetType(e.target.value)}
                  aria-label="When people join"
                  className="min-h-[34px] px-2 text-[13px] rounded-lg border border-slate-200 bg-white text-slate-900"
                >
                  <option value="pingOnEveryJoin">As each person joins</option>
                  <option value="pingOnFirstJoin">Only the first person</option>
                </select>
              }
            />
            <PrefRow
              title="When everyone leaves"
              control={
                <Switch
                  checked={settings.pingOnEmpty === true}
                  onCheckedChange={handleToggleEmpty}
                  aria-label="Text me when everyone leaves"
                />
              }
            />
          </>
        )}

        {!enabled && (
          <div className="px-4 py-4">
            <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
              Get text notifications about what happens in your Higlos.
            </p>
            <div className="flex gap-2 mb-3">
              <div className="w-24">
                <label
                  htmlFor={`sms-country-${familyID}`}
                  className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5"
                >
                  Country
                </label>
                <select
                  id={`sms-country-${familyID}`}
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    setPhoneError(null);
                  }}
                  className="w-full min-h-[38px] px-2 text-[16px] rounded-lg border border-slate-200 bg-white text-slate-900"
                >
                  {SUPPORTED_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {COUNTRY_FLAG[c.code] || c.code} {c.dial}
                    </option>
                  ))}
                </select>
              </div>
              <HField
                className="flex-1"
                label="Mobile number"
                htmlFor={`sms-phone-${familyID}`}
                type="tel"
                inputMode="tel"
                placeholder={selectedCountry?.placeholder}
                value={phoneInput}
                onChange={(e) => {
                  setPhoneInput(e.target.value);
                  setPhoneError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !saving) handleEnable();
                }}
                error={phoneError}
              />
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed mb-3">
              {consentTextFor(country)}
            </p>
            <div className="flex items-center justify-end gap-2">
              <HPrimaryButton onClick={handleEnable} disabled={saving}>
                {saving ? 'Saving…' : 'Turn on texts'}
              </HPrimaryButton>
            </div>
          </div>
        )}
      </HCard>

      {actionError && (
        <p className="text-[12px] text-rose-600 -mt-2 mb-4 px-1">{actionError}</p>
      )}
    </>
  );
}
